
import { useEffect, useState } from 'react'

import { fetchOrderAttributes } from '@/entities/attributes/thunks/fetchOrderAttributes'
import { driversSelectors, fetchDrivers } from '@/entities/drivers'
import { selectScenarioWithShiftsById } from '@/entities/schedules'
import { manageDrivers } from '@/entities/schedules/thunks/manageDrivers'
import { Button, Modal } from '@/shared/components'
import { GridTable } from '@/shared/components/GridTable/GridTable'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { timeHhMmToMins, timeMinsToHours } from '@/shared/lib/timeHelper'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { FormLabel, styled, Typography } from '@mui/material'
import { GridValidRowModel } from '@mui/x-data-grid-premium'

import { getManagerDriversDialogColumnDefs } from './libs/manageDriversDialogColumnDefs'

export type ManageDriversDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const ManageDriversDialog: React.FC<ManageDriversDialogProps> = ({
  open,
  onClose,
  scenarioId,
}) => {
  const [rows, setRows] = useState<GridValidRowModel[]>([])
  const [error, setError] = useState<boolean>(false)
  const [isLoading, setLoading] = useState(false)
  const scenario = useAppSelector(state => selectScenarioWithShiftsById(state, scenarioId))
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)
  const driverOptions = useAppSelector(state => driversSelectors.selectAll(state))
    .filter(driver => driver.groupIds.includes(activeGroupId) && driver.isActive)
    .map((driver) => {
      return {
        value: driver.id,
        label: driver.name,
      }
    })
  const dispatch = useAppDispatch()

  // Fetch drivers from server
  const fetchDriversFromServer = async () => {
    setLoading(true)
    await dispatch(fetchDrivers())
    setLoading(false)
  }

  useEffect(() => {
    fetchDriversFromServer()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // Columns
  const columns = getManagerDriversDialogColumnDefs(driverOptions)

  // Rows
  useEffect(() => {
    if (!scenario) return

    const initialRows = scenario.shifts.map(shift => {
      const shiftDuration = (timeHhMmToMins(shift?.finish?.plannedTime) || 0) - (timeHhMmToMins(shift?.start?.plannedTime) || 0)

      return {
        id: shift.id,
        description: shift.description,
        plannedStartTime: shift?.start?.plannedTime || '',
        estimatedFinishTime: shift?.finish?.plannedTime || '',
        shiftDuration: shiftDuration < 0 ? '' : timeMinsToHours(shiftDuration),
        orderCount: shift.orderCount,
        driver: shift.driverUserId,
      }
    })

    setRows(initialRows)
  }, [scenario])

  const onSubmit = async () => {
    if (!scenario || rows.length === 0) return
    setLoading(true)

    await dispatch(manageDrivers({
      scheduleId: scenario.id,
      shifts: rows.map(row => ({
        id: row.id,
        driverUserId: row?.driver || null,
      })),
      onSuccess: onClose,
    }))

    setLoading(false)
  }

  useEffect(() => {
    dispatch(fetchOrderAttributes())
  }, [dispatch])

  return (
    <Modal
      title={`Manage Drivers for Scenario: ${scenario?.name}`}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      disableClose={isLoading}
    >
      <StyledContent>
        <Typography variant="body1">Double click driver cell to update drivers:</Typography>
        <GridTable
          disableVirtualization
          loading={isLoading}
          rows={rows}
          columns={columns}
          processRowUpdate={(newRow) => {
            const updatedRows = rows.map(row => {
              if (row.id === newRow.id) {
                return newRow
              }

              return row
            })

            // Check for errors when two shifts have the same driver
            const driverIds = updatedRows.map(row => row.driver).filter(item => item)
            const uniqueDriverIds = [...new Set(driverIds)]

            if (driverIds.length !== uniqueDriverIds.length) {
              setError(true)
            } else {
              setError(false)
            }

            setRows(updatedRows)

            return newRow
          }}
          sx={{
            height: 340,
          }}
        />
        <FormLabel error sx={{ visibility: error ? 'visible' : 'hidden' }}>
          * Same driver has been assigned to multiple runs.
        </FormLabel>
      </StyledContent>
      <StyledFormActions>
        {onClose && <Button text onClick={onClose}>Cancel</Button>}
        <Button primary onClick={onSubmit}>
          Save
        </Button>
      </StyledFormActions>
    </Modal>
  )
}

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    min-width: ${(props) => props.theme.typography.pxToRem(540)};
    max-width: ${(props) => props.theme.typography.pxToRem(840)};
  }
`

const StyledFormActions = styled('div')`
  width: 100%;
  padding-bottom: ${(props) => props.theme.typography.pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${(props) => props.theme.typography.pxToRem(12)};
  padding-top: ${(props) => props.theme.typography.pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`